import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useCRM } from '../context/CRMContext';
import { useCRMData } from '../context/CRMDataContext';
import SafeIcon from '../common/SafeIcon';
import { formatCurrency, calculateDaysUntil } from '../utils/helpers';

// Import Sections
import ProjectOverview from '../components/projects/sections/ProjectOverview';
import StrategySection from '../components/projects/sections/StrategySection';
import PipelineSection from '../components/projects/sections/PipelineSection';
import WorkspaceSection from '../components/projects/sections/WorkspaceSection';
import DocumentationSection from '../components/projects/sections/DocumentationSection';
import PlaybookCanvas from '../components/playbook/PlaybookCanvas';

// Modals
import ProjectModal from '../components/projects/ProjectModal';

const getLocalStorageArray = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatExactCurrency = (value) => {
  const number = Number(value || 0);
  if (!number) return '0 NOK';
  return `${number.toLocaleString('nb-NO')} NOK`;
};

const formatProductPrice = (product) => {
  const pricingModel = product?.pricingModel || 'fixed';
  const price = product?.projectPrice ?? product?.price;
  const fixedPrice = product?.projectFixedPrice ?? product?.fixedPrice;
  const percentageRate = product?.projectPercentageRate ?? product?.percentageRate;

  if (pricingModel === 'custom') return 'Etter avtale';

  if (pricingModel === 'commission-percent' || pricingModel === 'value-percent') {
    return `${percentageRate || 0} %`;
  }

  if (pricingModel === 'combination') {
    return `${formatExactCurrency(fixedPrice || 0)} + ${percentageRate || 0} %`;
  }

  const units = {
    hourly: 'time',
    monthly: 'måned',
    'per-user': 'bruker',
    'per-lead': 'lead',
    'per-meeting': 'møte',
    'per-ticket': 'sak/ticket'
  };

  const formattedPrice = formatExactCurrency(price || 0);
  return units[pricingModel] ? `${formattedPrice} / ${units[pricingModel]}` : formattedPrice;
};

function SelectedProductsSection({ project }) {
  const selectedProducts = Array.isArray(project?.selectedProducts)
    ? project.selectedProducts
    : [];
  const estimatedProjectValue = Number(project?.estimatedProjectValue || 0);
  const hasPercentagePricing = Boolean(project?.hasPercentagePricing);

  return (
    <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
        <div>
          <h2 className="text-lg font-black text-slate-900">Produkter &amp; tjenester</h2>
          <p className="text-sm text-slate-500 mt-1">
            Produkter, tjenester og prismodeller valgt for dette prosjektet.
          </p>
        </div>
        <div className="text-sm font-bold text-slate-600">
          {selectedProducts.length} produkter/tjenester valgt
        </div>
      </div>

      {estimatedProjectValue > 0 && (
        <div className="mb-5 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">
            Estimert verdi
          </p>
          <p className="text-xl font-black text-emerald-800">
            {formatExactCurrency(estimatedProjectValue)}
          </p>
        </div>
      )}

      {hasPercentagePricing && (
        <p className="mb-5 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
          Inneholder prosent/provisjon som beregnes senere i tilbud/kontrakt.
        </p>
      )}

      {selectedProducts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
          Ingen produkter eller tjenester er valgt for dette prosjektet.
        </div>
      ) : (
        <div className="space-y-3">
          {selectedProducts.map((product, index) => (
            <div
              key={product.productId || product.id || `${product.name}-${index}`}
              className="rounded-xl border border-slate-200 p-4"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-bold text-slate-900">{product.name || 'Produkt/tjeneste'}</h3>
                  {product.description && (
                    <p className="text-sm text-slate-500 mt-1">{product.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {[product.productType, product.category, product.pricingModel].filter(Boolean).map((label) => (
                      <span
                        key={label}
                        className="px-2 py-1 rounded-md bg-slate-100 text-[11px] font-semibold text-slate-600"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
                <p className="shrink-0 text-sm font-black text-slate-900">
                  {formatProductPrice(product)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const normalizeSupabaseProject = (project) => ({
  ...project,
  source: 'supabase',
  projectType: project.project_type,
  projectFor: project.project_for,
  startDate: project.start_date,
  endDate: project.end_date,
  target: project.target_value,
  targetValue: project.target_value,
  pricingModel: project.pricing_model,
  agreedPrice: project.agreed_price,
  isInternal: project.is_internal,
  clientCompanyId: project.client_company_id
});

export default function ProjectCanvas() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { updateProject: updateLegacyProject } = useCRM();
  const {
    projects,
    updateProject,
    clientPipelines,
    addClientPipeline
  } = useCRMData();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [actionError, setActionError] = useState('');
  const [isActionBusy, setIsActionBusy] = useState(false);
  const [isPipelineActionBusy, setIsPipelineActionBusy] = useState(false);
  const [statusOverride, setStatusOverride] = useState(null);

  const activeSection = searchParams.get('section') || 'overview';
  const localProjects = getLocalStorageArray('saleswork_demo_projects');
  const supabaseProjects = Array.isArray(projects)
    ? projects.map(normalizeSupabaseProject)
    : [];
  const routeId = String(projectId || '');
  const supabaseProject = supabaseProjects.find((item) => String(item.id || '') === routeId);
  const legacyProject = localProjects.find((item) => (
    String(item.id || '') === routeId ||
    String(item.project_id || '') === routeId ||
    String(item.projectId || '') === routeId
  ));
  const project = supabaseProject || legacyProject;
  const isSupabaseProject = Boolean(supabaseProject);

  useEffect(() => {
    setStatusOverride(null);
    setActionError('');
  }, [project?.id]);

  if (!project) return <div className="p-8">Prosjekt ikke funnet.</div>;

  const projectPipeline = Array.isArray(clientPipelines)
    ? clientPipelines.find((pipeline) => (
      String(pipeline.project_id || pipeline.projectId || '') === String(project.id || '')
    ))
    : null;
  const projectOwnerCompanyId = project.client_company_id || project.clientCompanyId || null;
  const isInternalProject = project.is_internal === true || project.isInternal === true;
  const canCreateProjectPipeline = isSupabaseProject;

  const displayedStatus = statusOverride || project.status || 'Planlagt';
  const normalizedStatus = String(displayedStatus).toLowerCase();

  const handleSectionChange = (section) => {
    setSearchParams({ section });
  };

  const handleCreateProjectPipeline = async () => {
    if (isPipelineActionBusy || projectPipeline) return;

    if (!canCreateProjectPipeline) {
      setActionError('Pipeline kan bare opprettes for et Supabase-prosjekt med en gyldig oppdragsgiver.');
      return;
    }

    setActionError('');
    setIsPipelineActionBusy(true);

    try {
      const result = await addClientPipeline({
        project_id: project.id,
        owner_company_id: projectOwnerCompanyId,
        name: `${project.name} Pipeline`,
        status: 'Active',
        description: `Pipeline for ${project.name}`
      });

      if (!result?.success) {
        console.error('[ProjectCanvas] Pipeline kunne ikke opprettes.', result);
        setActionError(result?.error?.message || result?.error || 'Pipeline kunne ikke opprettes.');
      }
    } catch (error) {
      console.error('[ProjectCanvas] Uventet feil ved opprettelse av pipeline.', error);
      setActionError(error?.message || 'Pipeline kunne ikke opprettes.');
    } finally {
      setIsPipelineActionBusy(false);
    }
  };

  const handleUpdateProject = async (updatedData) => {
    if (isSupabaseProject) {
      const result = await updateProject(project.id, {
        name: updatedData.name,
        description: updatedData.description,
        project_type: updatedData.projectType || updatedData.project_type,
        project_for: updatedData.projectFor || updatedData.project_for,
        status: updatedData.status,
        start_date: updatedData.startDate || updatedData.start_date || null,
        end_date: updatedData.endDate || updatedData.end_date || null,
        budget: updatedData.budget,
        target_value: updatedData.targetValue || updatedData.target_value,
        target_unit: updatedData.targetUnit || updatedData.target_unit,
        target_period: updatedData.targetPeriod || updatedData.target_period,
        pricing_model: updatedData.pricingModel || updatedData.pricing_model,
        agreed_price: updatedData.agreedPrice ?? updatedData.agreed_price ?? null,
        is_internal: updatedData.isInternal ?? updatedData.is_internal,
        client_company_id: updatedData.clientCompanyId ?? updatedData.client_company_id ?? null
      });

      if (!result?.success) {
        console.error('[ProjectCanvas] Supabase-prosjektet ble ikke oppdatert.', result);
        return;
      }
    } else {
      updateLegacyProject(project.id, updatedData);
    }

    setIsEditModalOpen(false);
  };

  const handleStatusUpdate = async (nextStatus) => {
    if (isActionBusy) return;

    setActionError('');
    setIsActionBusy(true);

    try {
      if (isSupabaseProject) {
        const result = await updateProject(project.id, { status: nextStatus });

        if (!result?.success) {
          console.error('[ProjectCanvas] Statusendring feilet.', result);
          setActionError(result?.error?.message || result?.error || 'Statusendringen kunne ikke lagres.');
          return;
        }
      } else {
        updateLegacyProject(project.id, { ...project, status: nextStatus });
      }

      setStatusOverride(nextStatus);
    } catch (error) {
      console.error('[ProjectCanvas] Uventet feil ved statusendring.', error);
      setActionError(error?.message || 'Statusendringen kunne ikke lagres.');
    } finally {
      setIsActionBusy(false);
    }
  };

  const handleCloseProject = async () => {
    const confirmed = window.confirm(
      'Er du sikker på at du vil avslutte prosjektet? Statusen blir satt til «Avsluttet».'
    );

    if (!confirmed) return;

    const wantsReport = window.confirm(
      'Vil du lage sluttrapport for prosjektet nå?'
    );

    if (wantsReport) {
      window.alert('Sluttrapport kommer i neste versjon.');
    }

    await handleStatusUpdate('Avsluttet');
  };

  const handleDeactivateProject = async () => {
    const confirmed = window.confirm(
      'Prosjektet slettes ikke fysisk. Det blir bare deaktivert og får status «Slettet». Vil du fortsette?'
    );

    if (confirmed) {
      await handleStatusUpdate('Slettet');
    }
  };

  const renderStatusActions = () => {
    if (normalizedStatus === 'slettet') return null;

    const actions = [];

    if (normalizedStatus === 'planlagt' || normalizedStatus === 'aktiv') {
      actions.push({ label: 'Sett på pause', status: 'På pause' });
      actions.push({ label: 'Arkiver', status: 'Arkivert' });
      actions.push({ label: 'Avslutt prosjekt', onClick: handleCloseProject });
    }

    if (normalizedStatus === 'på pause') {
      actions.push({ label: 'Gjenoppta', status: 'Aktiv' });
      actions.push({ label: 'Arkiver', status: 'Arkivert' });
      actions.push({ label: 'Avslutt prosjekt', onClick: handleCloseProject });
    }

    if (normalizedStatus === 'arkivert') {
      actions.push({ label: 'Hent fra arkiv / Reaktiver', status: 'Aktiv' });
    }

    if (normalizedStatus === 'avsluttet') {
      actions.push({ label: 'Lag sluttrapport', onClick: () => window.alert('Sluttrapport kommer i neste versjon.') });
      actions.push({ label: 'Arkiver', status: 'Arkivert' });
    }

    actions.push({ label: 'Slett/deaktiver', onClick: handleDeactivateProject });

    return actions.map((action) => (
      <button
        key={action.label}
        type="button"
        disabled={isActionBusy}
        onClick={() => action.onClick ? action.onClick() : handleStatusUpdate(action.status)}
        className="px-3 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-[10px] font-bold uppercase tracking-wider hover:bg-slate-50 transition-all shadow-sm disabled:opacity-50"
      >
        {action.label}
      </button>
    ));
  };

  const daysLeft = project.end_date ? calculateDaysUntil(project.end_date) : 0;

  const navItems = [
    { id: 'overview', label: 'Oversikt & Kunde', icon: '📋' },
    { id: 'playbook', label: 'Playbook Engine', icon: '📖' },
    { id: 'crm', label: 'Salgsmuligheter', icon: '💰' },
    { id: 'workspace', label: 'Team Chat', icon: '💬' },
    { id: 'documentation', label: 'Filer', icon: '📂' },
  ];

  const renderProjectPipeline = () => (
    <section className="mb-6 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">Pipeline</p>
          {projectPipeline ? (
            <>
              <h2 className="mt-1 text-lg font-black text-slate-900">{projectPipeline.name}</h2>
              <p className="mt-1 text-sm text-slate-500">
                Status: {projectPipeline.status || 'Active'}
              </p>
            </>
          ) : (
            <p className="mt-1 text-sm text-slate-500">
              Prosjektet har ingen pipeline ennå.
            </p>
          )}
        </div>

        {projectPipeline ? (
          <button
            type="button"
            onClick={() => navigate(`/pipeline?project_id=${encodeURIComponent(project.id)}&pipeline_id=${encodeURIComponent(projectPipeline.id)}`)}
            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-slate-50 transition-all shadow-sm"
          >
            Åpne pipeline
          </button>
        ) : (
          <div className="flex flex-col items-start sm:items-end gap-2">
            <button
              type="button"
              onClick={handleCreateProjectPipeline}
              disabled={isPipelineActionBusy || !canCreateProjectPipeline}
              className="px-4 py-2 bg-primary-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-primary-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPipelineActionBusy ? 'Oppretter...' : 'Opprett pipeline for prosjektet'}
            </button>
          </div>
        )}
      </div>
    </section>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <>
            <ProjectOverview project={project} onEdit={() => setIsEditModalOpen(true)} />
            <SelectedProductsSection project={project} />
          </>
        );
      case 'playbook': return <PlaybookCanvas projectId={project.project_id || project.id} />;
      case 'strategy': return <StrategySection project={project} />;
      case 'crm': return <PipelineSection project={project} />;
      case 'workspace': return <WorkspaceSection project={project} />;
      case 'documentation': return <DocumentationSection project={project} />;
      default: return <ProjectOverview project={project} onEdit={() => setIsEditModalOpen(true)} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      {/* Premium Header Banner */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">

          {/* Top Row: Navigation & Title */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-6">
            <div className="flex items-center gap-4 text-slate-800">
              <button
                onClick={() => navigate('/projects')}
                className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-slate-600"
              >
                <SafeIcon icon="⬅️" className="w-6 h-6" />
              </button>
              <div>
                <div className="flex items-center space-x-2 text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">
                  <span className="bg-slate-100 px-2 py-0.5 rounded text-slate-500">{displayedStatus}</span>
                  <span>•</span>
                  <span className={daysLeft < 10 ? 'text-red-500' : 'text-slate-500'}>
                    {daysLeft > 0 ? `${daysLeft} dager igjen` : 'Frist utløpt eller ingen dato'}
                  </span>
                </div>
                <h1 className="text-3xl font-black text-slate-900 tracking-tight">{project.name}</h1>
              </div>
            </div>

            {/* Key Metrics / Actions */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setIsEditModalOpen(true)}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm flex items-center gap-2"
              >
                <SafeIcon icon="✏️" className="w-3 h-3" /> Rediger
              </button>

              {renderStatusActions()}

              <div className="hidden md:block w-px h-8 bg-slate-200 mx-2"></div>

              <div className="px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Budsjett</p>
                <p className="text-lg font-black text-slate-900">{formatCurrency(project.budget || 0, true)}</p>
              </div>
              <div className="hidden md:block px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Mål</p>
                <p className="text-lg font-black text-emerald-600">{formatCurrency(project.target || 0, true)}</p>
              </div>
            </div>
          </div>

          {actionError && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {actionError}
            </div>
          )}

          {/* Navigation Tabs */}
          <div className="flex space-x-1 overflow-x-auto no-scrollbar">
            {navItems.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleSectionChange(item.id)}
                  className={`flex items-center px-6 py-4 rounded-t-xl text-xs font-black uppercase tracking-widest transition-all border-b-2 whitespace-nowrap ${
                    isActive
                      ? 'border-primary-600 text-primary-700 bg-primary-50/50'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <SafeIcon icon={item.icon} className={`mr-2.5 w-4 h-4 ${isActive ? 'text-primary-600' : 'text-slate-400'}`} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {renderProjectPipeline()}
        {renderContent()}
      </div>

      {/* Global Project Edit Modal */}
      <ProjectModal
        isOpen={isEditModalOpen}
        project={project}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleUpdateProject}
      />
    </div>
  );
}